import { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import axios from "axios";
import { useSnackbar } from "notistack";
import { CloseIcon } from "../../../minimals/theme/overrides/CustomIcons";
import { NewTask, OmitOption } from "./types";
import { SelectedTasks } from "../types";
import { statusColorMap } from "../statusColorMap";

type MultiStatusUpdateModalProps = {
  open: boolean;
  onClose: () => void;
  selectedTasks: SelectedTasks;
  refreshTasks: () => Promise<void>;
  onUpdated: () => void;
  tasks: NewTask[];
};

export default function MultiStatusUpdateModal({
  open,
  onClose,
  selectedTasks,
  refreshTasks,
  onUpdated,
  tasks
}: MultiStatusUpdateModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedStatus, setSelectedStatus] = useState<OmitOption | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedTaskObjects = useMemo(() => tasks.filter((t) => selectedTasks[t.id]), [selectedTasks, tasks]);
  const tasksNotUpdatedableToComplete = useMemo(
    () => selectedTaskObjects.filter((t) => t.isDocUploadRequired && t.documents.length === 0),
    [selectedTaskObjects]
  );

  const tasksToUpdate = useMemo(
    () =>
      selectedStatus?.statusApplicableTo === 3
        ? selectedTaskObjects.filter((t) => !t.isDocUploadRequired || t.documents.length > 0)
        : selectedTaskObjects,
    [selectedStatus]
  );

  const commonStatusOptions = useMemo(() => {
    if (selectedTaskObjects.length === 0) return [];

    return selectedTaskObjects.slice(1).reduce((acc, currentTask) => {
      return acc.filter((opt) => currentTask.omitOptions.some((o) => o.id === opt.id));
    }, selectedTaskObjects[0].omitOptions);
  }, [selectedTaskObjects]);

  const handleClose = () => {
    if (isSubmitting) return;
    setSelectedStatus(null);
    onClose();
  };

  const handleSave = async () => {
    if (!selectedStatus?.id || tasksToUpdate.length === 0) return;

    setIsSubmitting(true);
    try {
      await Promise.all(
        tasksToUpdate.map((task) =>
          axios.post("/api/taskmanagement/newtasks/updateStatus", {
            basicTaskId: task.id,
            status: selectedStatus.id
          })
        )
      );
      await refreshTasks();
      onUpdated?.();
      setIsSubmitting(false);
      handleClose();
    } catch (error) {
      enqueueSnackbar("Unable to update status. Please try again.", { variant: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasSharedOptions = commonStatusOptions.length > 0;
  const selectedCount = selectedTaskObjects.length;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Typography variant="subtitle1">
          Update status for {selectedCount} task{selectedCount === 1 ? "" : "s"}
        </Typography>
      </DialogTitle>
      <IconButton
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500]
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography color="text.secondary">
            Choose a status to apply to all selected tasks. Only statuses shared between the selections are available.
          </Typography>

          {selectedCount > 0 && (
            <Stack direction="column" gap={1}>
              {selectedTaskObjects.map((task) => (
                <Box>
                  <Chip key={task.id} label={task.description} size="small" />
                </Box>
              ))}
            </Stack>
          )}

          {selectedStatus?.statusApplicableTo === 3 && tasksNotUpdatedableToComplete.length > 0 && (
            <Alert severity="error">
              <Typography variant="subtitle1" marginBottom={1}>
                These tasks require a document upload. Please update them individually.
              </Typography>
              <Stack direction="column" gap={1}>
                {tasksNotUpdatedableToComplete.map((t) => (
                  <Box>
                    <Chip key={t.id} label={t.description} size="small" />
                  </Box>
                ))}
              </Stack>
            </Alert>
          )}

          {hasSharedOptions ? (
            <Autocomplete
              options={commonStatusOptions}
              value={selectedStatus}
              onChange={(_, option) => setSelectedStatus(option)}
              getOptionLabel={(option) => option.text}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Status"
                  sx={{
                    "& .MuiInputBase-input": {
                      color: statusColorMap[selectedStatus?.statusApplicableTo || ""],
                      fontSize: "0.875rem",
                      padding: "4px 8px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }
                  }}
                />
              )}
              renderOption={(props, option, { index }) => (
                <li
                  {...props}
                  key={option.id}
                  style={{
                    backgroundColor: "#f5f5f5",
                    transition: "background-color 0.3s",
                    cursor: "pointer"
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e0e0e0")}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f5f5f5")}
                >
                  <Typography color={statusColorMap[option.statusApplicableTo || ""]} sx={{ overflowWrap: "anywhere" }}>
                    {option.text}
                  </Typography>
                </li>
              )}
            />
          ) : (
            <Typography color="text.secondary">No shared statuses available for the selected tasks.</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="inherit"
          disabled={isSubmitting || !selectedStatus?.id || tasksToUpdate.length === 0}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!selectedStatus?.id || isSubmitting || !hasSharedOptions}
        >
          {isSubmitting ? "Updating..." : "Update Status"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
